import { createTestId, suffixTestId } from "@spawn/common";
import { ScaleProvider } from "@spawn/core";
import { TextAutoScale, useObservable, usePlatformDetect, useService } from "@spawn/modern-ui";
import css from "@spawn/modern-ui/src/components/page/components/pageSymbols/pageSymbols.scss";
import { CurrencyService, NetworkService } from "@spawn/slot-machine";
import classNames from "classnames";
import * as React from "react";
import Url from "url-parse";

export interface IPageSymbolsSymbol {
	symbolName: string;
	/** URL from the symbol image. */
	symbolUrl: Url<string>;
	payoutValues?: {
		/**
		 * Define a label next to the multiplier. This can be optional, because there
		 * are some cases where only a multiplier needs to be shown.
		 */
		label: string;
		/**
		 * The multiplier will be given to our {@link CurrencyService} the calculate the
		 * output in the correct currency and format.
		 */
		multiplier?: number;
	}[];
}

export interface IPageSymbols {
	/** Define symbol who should be shown in a 4x grid.  */
	symbols: IPageSymbolsSymbol[];
	/** When true only 2 symbols are shown in a row. Also symbols are shown in a horizontal layout. */
	isHorizontal?: boolean;
}

/**
 * Displays 4 symbols in row. When `isHorizontal` is true,
 * 2 symbol are shown in a row in horizontal style.
 *
 * @returns {JSX.Element}
 */
export const BonusSymbolsSection = ({ symbols, isHorizontal }: IPageSymbols): JSX.Element => {
	const { isMobile } = usePlatformDetect();
	const currencyService = useService(CurrencyService);
	const networkService = useService(NetworkService);
	const scaleProvider = useService(ScaleProvider);
	const currentBetFactor = useObservable(networkService.currentBetFactor$, 0);
	const uiScaleFactor = useObservable(scaleProvider.uiScaleFactor$, 0);
	const payoutValuesTestId = createTestId("payoutValues");
	const ref = React.useRef<HTMLDivElement>(null);
	const [imageHeight, setImageHeight] = React.useState(0);
	const [loadedImages, setLoadedImages] = React.useState(0);

	React.useEffect(() => {
		// get all height of the symbols images
		const query = `[class='${css.symbolImage}']`;
		const symbolsImages = ref.current?.querySelectorAll(query);
		// get height of the highest image
		const highestImage = Math.max(...Array.from(symbolsImages || []).map((image) => (image as HTMLImageElement).clientHeight), 0);
		// set height of all images to the highest image
		setImageHeight(highestImage);
	}, [uiScaleFactor, loadedImages]);

	return (
		<div
			className={classNames(css.symbols, {
				[css.isHorizontal]: isHorizontal,
				[css.isMobile]: isMobile,
				[css.isAlone]: symbols.length === 1,
			})}
			ref={ref}
			style={{
				...(isMobile &&
					isHorizontal && {
						gridTemplateColumns: "1fr 1fr",
					}),
			}}
		>
			{symbols.map((symbol) => {
				return (
					<div
						key={symbol.symbolName}
						className={css.symbol}
						style={{
							gridTemplateRows: `${imageHeight === 0 ? "1fr" : imageHeight + "px"} max-content`,
							gridTemplateColumns: "70% 30%",
							justifyItems: "start",
						}}
					>
						<img
							className={css.symbolImage}
							src={symbol.symbolUrl.toString()}
							onLoad={() => setLoadedImages(loadedImages + 1)}
							alt={symbol.symbolName}
							style={{ placeSelf: "center" }}
						/>
						<div
							className={css.payoutValues}
							style={{ display: "flex", flexDirection: "column" }}
							data-testid={suffixTestId(payoutValuesTestId, symbol.symbolName)}
						>
							{symbol.payoutValues?.map((payoutValue) => {
								return (
									<React.Fragment key={payoutValue.label}>
										{payoutValue.label ? (
											<div
												className={css.multiplier}
												data-testid={suffixTestId(payoutValuesTestId, symbol.symbolName, "payoutLabel", payoutValue.label)}
											>
												<TextAutoScale paramNameMobile="pageTextFontSizeMobile" paramNameDesktop="pageTextFontSizeDesktop">
													{payoutValue.label}
												</TextAutoScale>
											</div>
										) : (
											<></>
										)}
										{payoutValue.multiplier ? (
											<div className={css.multiplier}>
												<TextAutoScale paramNameMobile="pageTextFontSizeMobile" paramNameDesktop="pageTextFontSizeDesktop">
													{currencyService.getLocalized(payoutValue.multiplier * currentBetFactor)}
												</TextAutoScale>
											</div>
										) : (
											<></>
										)}
									</React.Fragment>
								);
							})}
						</div>
					</div>
				);
			})}
		</div>
	);
};
